import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { userService } from '../../services'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../auth/auth.context'
import PageLayout from '../../components/layout/page-layout'
import { FormContainer, FormInput, FormSelect, FormActions } from '../../components/forms'
import { Container } from '../../components/ui/container'
import { AlertCircle, Info } from 'lucide-react'

export default function CreateUserPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id: companyIdFromRoute } = useParams()

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: (companyIdFromRoute ? 'ADMIN' : 'EMPLOYEE') as 'EMPLOYEE' | 'ADMIN',
    startDate: undefined as Date | undefined,
    function: undefined as 'IT' | 'HR' | 'Lager' | 'Putzkraft' | 'Pflege' | undefined,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const createUserMutation = useMutation({
    mutationFn: (userData: any) => userService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['company-users'] })
      if (companyIdFromRoute) {
        navigate(`/companies/${companyIdFromRoute}/users`)
      } else {
        navigate('/users')
      }
    },
    onError: (error: any) => {
      setError(error?.response?.data?.message ?? t('users.createUserError'))
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(undefined)

    if (formData.role === 'EMPLOYEE') {
      const hasEmail = !!formData.email?.trim()
      const hasPhone = !!formData.phoneNumber?.trim()
      if (!hasEmail && !hasPhone) {
        setError(t('users.loginIdentityRequired'))
        return
      }
    }

    setLoading(true)
    try {
      const userData = {
        email:
          formData.role === 'EMPLOYEE'
            ? formData.email?.trim() || undefined
            : formData.email?.trim(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber || undefined,
        role: formData.role,
        companyId: companyIdFromRoute || user?.companyId,
        isActive: true,
        startDate: formData.startDate || undefined,
        function: formData.function || undefined,
      }
      await createUserMutation.mutateAsync(userData)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    if (name === 'startDate') {
      setFormData(prev => ({ ...prev, [name]: value ? new Date(value) : undefined }))
    } else if (name === 'function') {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? undefined : (value as typeof prev.function),
      }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const roleOptions = useMemo(
    () => [
      { value: 'EMPLOYEE', label: t('roles.employee') },
      { value: 'ADMIN', label: t('roles.admin') },
    ],
    [t]
  )

  const functionOptions = useMemo(
    () => [
      { value: '', label: t('common.noSelection') },
      { value: 'IT', label: t('employee.functions.it') },
      { value: 'HR', label: t('employee.functions.hr') },
      { value: 'Lager', label: t('employee.functions.warehouse') },
      { value: 'Putzkraft', label: t('employee.functions.cleaning') },
      { value: 'Pflege', label: t('employee.functions.care') },
    ],
    [t]
  )

  return (
    <PageLayout
      isLoading={createUserMutation.isPending}
      error={createUserMutation.error}
      loadingMessage={t('users.creatingUser')}
      errorMessage={error || t('users.createUserError')}
    >
      <Container>
        <FormContainer
          title={companyIdFromRoute ? t('users.inviteAdmin') : t('users.createNewEmployee')}
          subtitle={
            companyIdFromRoute ? t('users.inviteAdminSubtitle') : t('users.createEmployeeSubtitle')
          }
          onSubmit={handleSubmit}
        >
          <div className='space-y-6'>
            {error && (
              <div className='flex items-start gap-2 p-3 bg-error-50 border border-error-100 rounded-lg'>
                <AlertCircle className='w-4 h-4 text-error-500 mt-0.5 flex-shrink-0' />
                <p className='text-sm text-error-700'>{error}</p>
              </div>
            )}

            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              <FormInput
                label={t('common.firstName')}
                name='firstName'
                value={formData.firstName}
                onChange={handleChange}
                placeholder='Max'
                required
              />
              <FormInput
                label={t('common.lastName')}
                name='lastName'
                value={formData.lastName}
                onChange={handleChange}
                placeholder='Mustermann'
                required
              />
            </div>

            <FormInput
              label={t('common.email')}
              name='email'
              type='email'
              value={formData.email}
              onChange={handleChange}
              placeholder={t('users.emailPlaceholder')}
              required={formData.role !== 'EMPLOYEE'}
              helperText={
                formData.role === 'EMPLOYEE'
                  ? t('users.loginIdentityHelper')
                  : t('users.emailRequiredAdmin')
              }
            />

            <FormInput
              label={t('common.phone')}
              name='phoneNumber'
              type='tel'
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder='+49 123 456789'
              helperText={
                formData.role === 'EMPLOYEE'
                  ? t('users.loginIdentityHelper')
                  : t('users.phoneOptionalAdmin')
              }
            />

            {formData.role === 'EMPLOYEE' && (
              <p className='text-sm text-neutral-600 -mt-2'>{t('users.loginIdentityHint')}</p>
            )}

            {companyIdFromRoute ? (
              <FormSelect
                label={t('common.role')}
                name='role'
                value={formData.role}
                onChange={handleChange}
                options={[{ value: 'ADMIN', label: t('roles.admin') }]}
                disabled
                required
              />
            ) : (
              <FormSelect
                label={t('common.role')}
                name='role'
                value={formData.role}
                onChange={handleChange}
                options={roleOptions}
                required
              />
            )}

            <div className='flex items-start gap-2 p-3 bg-neutral-50 border border-neutral-100 rounded-lg'>
              <Info className='w-4 h-4 text-neutral-400 mt-0.5 flex-shrink-0' />
              <p className='text-sm text-neutral-600'>
                {formData.role === 'ADMIN'
                  ? t('users.adminPasswordHint')
                  : t('users.employeeOtpHint')}
              </p>
            </div>

            <FormInput
              label={t('common.startDate')}
              name='startDate'
              type='date'
              value={
                formData.startDate ? formData.startDate.toISOString().split('T')[0] : undefined
              }
              onChange={handleChange}
            />

            <FormSelect
              label={t('common.function')}
              name='function'
              value={formData.function}
              onChange={handleChange}
              options={functionOptions}
            />

            <FormActions
              onCancel={() =>
                companyIdFromRoute
                  ? navigate(`/companies/${companyIdFromRoute}/users`)
                  : navigate('/users')
              }
              submitText={
                companyIdFromRoute ? t('users.inviteAdmin') : t('users.createEmployeeButton')
              }
              isLoading={loading || createUserMutation.isPending}
            />
          </div>
        </FormContainer>
      </Container>
    </PageLayout>
  )
}
